import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Gavel, ListChecks, NotebookPen, Search, StickyNote } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api } from "@/lib/api";

const GROUP_META = {
  matters: { icon: Gavel, dot: "rgb(var(--c-heat))" },
  events: { icon: CalendarDays, dot: "rgb(var(--c-session-2))" },
  logs: { icon: NotebookPen, dot: "rgb(var(--c-fixed))" },
  todos: { icon: ListChecks, dot: "rgb(var(--c-session-1))" },
  postits: { icon: StickyNote, dot: "rgb(var(--c-oos))" },
};

export const CommandPalette = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setGroups([]);
    }
  }, [open]);

  const run = useCallback(async (text) => {
    if (text.trim().length < 2) {
      setGroups([]);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.get("/search", { params: { q: text.trim() } });
      setGroups(data.groups);
    } catch {
      setGroups([]);
    } finally {
      setBusy(false);
    }
  }, []);

  const onType = (text) => {
    setQuery(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run(text), 220);
  };

  const go = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="command-palette"
        className="p-0 max-w-2xl rounded-sm overflow-hidden border-border bg-card/85 backdrop-blur-xl shadow-2xl"
      >
        <DialogTitle className="sr-only">Search the chambers diary</DialogTitle>
        <DialogDescription className="sr-only">
          Search matters, diary entries, hearing logs, to-dos and post-its.
        </DialogDescription>
        <Command shouldFilter={false} className="bg-transparent">
          <CommandInput
            data-testid="command-palette-input"
            value={query}
            onValueChange={onType}
            placeholder="Search matters, diary entries, hearing logs, to-dos, post-its…"
            className="text-base h-14"
          />
          <CommandList className="max-h-[26rem]">
            {query.trim().length < 2 ? (
              <div className="px-4 py-8 text-sm text-muted-foreground" data-testid="command-hint">
                Type at least two letters. Case numbers, party names, order text and note bodies are
                all searched.
              </div>
            ) : busy && !groups.length ? (
              <div className="px-4 py-8 text-sm text-muted-foreground">Searching…</div>
            ) : (
              <CommandEmpty data-testid="command-empty">Nothing found for “{query}”.</CommandEmpty>
            )}
            {groups.map((group) => {
              const meta = GROUP_META[group.kind] || GROUP_META.matters;
              const Icon = meta.icon;
              return (
                <CommandGroup
                  key={group.kind}
                  heading={group.label}
                  data-testid={`command-group-${group.kind}`}
                >
                  {group.items.map((item) => (
                    <CommandItem
                      key={`${group.kind}-${item.id}`}
                      value={`${group.kind}-${item.id}`}
                      onSelect={() => go(item.path)}
                      data-testid={`command-result-${item.id}`}
                      className="gap-3 py-2.5"
                    >
                      <span
                        aria-hidden
                        className="h-2 w-2 rounded-full shrink-0"
                        style={{ background: meta.dot }}
                      />
                      <Icon className="h-4 w-4 shrink-0 opacity-70" />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{item.title}</span>
                        {item.sub ? (
                          <span className="block truncate text-xs text-muted-foreground">
                            {item.sub}
                          </span>
                        ) : null}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};

export const SearchTrigger = ({ onClick }) => (
  <>
    <button
      data-testid="global-search-trigger"
      onClick={onClick}
      className="hidden xl:flex items-center gap-2 h-10 px-3 rounded-sm border border-border bg-background/60 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-200 xl:min-w-[15rem]"
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="text-sm truncate">Search matters, entries…</span>
      <kbd className="ml-auto text-[0.7rem] font-mono border border-border rounded px-1.5 py-0.5">
        ⌘K
      </kbd>
    </button>
    <button
      data-testid="global-search-trigger-mobile"
      onClick={onClick}
      className="xl:hidden h-10 w-10 grid place-items-center rounded-sm border border-border hover:bg-accent transition-colors duration-200"
      aria-label="Search"
    >
      <Search className="h-5 w-5" />
    </button>
  </>
);
